import { resolveApiBaseUrl } from "@/utils/tracking";
import {
  getRecaptchaToken,
  getRecaptchaTokenResult,
  invalidateRecaptchaPublicConfig,
  isRecaptchaClientSetupErrorMessage,
  prefetchRecaptchaPublicConfig,
} from "@/utils/recaptcha";

/** Baza API include `/api` (și pentru REACT_APP_API_ROOT-only — vezi `tracking.js`). */
function apiBase() {
  return resolveApiBaseUrl({ forTracking: false }).replace(/\/+$/, "");
}

function endpoint(file) {
  return `${apiBase()}/${file}`;
}

/**
 * Normalize PHP JSON (unele răspunsuri folosesc `ok`, MVP-ul `success`).
 * @param {string} [requestUrl] — pentru mesaje de debug dacă răspunsul nu e JSON
 */
async function readVinPartsResponse(res, requestUrl = "") {
  let text = "";
  try {
    text = await res.text();
  } catch {
    text = "";
  }

  const jsonPayload = text.replace(/^\uFEFF/, "").trim();

  let body = {};
  if (jsonPayload) {
    try {
      body = JSON.parse(jsonPayload);
    } catch {
      const snippet = jsonPayload.replace(/\s+/g, " ").slice(0, 200);
      const hint =
        res.status === 404
          ? " Probabil URL-ul API nu include folderul `/api` (ex.: folosește `http://localhost:8000/api` în REACT_APP_API_BASE_URL)."
          : "";
      body = {
        success: false,
        error: `Răspuns ne-JSON de la server (HTTP ${res.status}).${hint}${
          requestUrl ? ` URL: ${requestUrl}.` : ""
        } Început răspuns: «${snippet}»`,
      };
    }
  } else if (!res.ok) {
    body = {
      success: false,
      error: `Serverul a răspuns fără corp (${res.status}).`,
    };
  }

  const explicitFail = body.success === false || body.ok === false;
  const explicitSuccess = body.success === true || body.ok === true;
  const inferredSuccess =
    !explicitFail &&
    (body.vehicle != null ||
      Array.isArray(body.parts) ||
      Array.isArray(body.products));

  const success = res.ok && !explicitFail && (explicitSuccess || inferredSuccess);

  const message =
    typeof body.error === "string" && body.error.trim()
      ? body.error.trim()
      : null;

  const error =
    message ||
    (!success
      ? res.status === 403
        ? body.origin
          ? "Acces respins (Origin nepermis pentru API). Adaugă domeniul în api/config.php → allowed_origins."
          : "Verificare anti-bot respinsă sau lipsă. Reîncarcă pagina și încearcă din nou."
        : res.status === 502
          ? "Decode-ul eșuat (server). Activează vin_parts.enabled, construiește tools/vin-scraper și rulează playwright install chromium."
          : `Cererea a eșuat (HTTP ${res.status}).`
      : undefined);

  return {
    ok: success,
    status: res.status,
    data: {
      ...body,
      success,
      ...(error ? { error } : {}),
    },
  };
}

export async function postDecodeVin(vin) {
  const url = endpoint("decode-vin.php");

  const postOnce = async () => {
    invalidateRecaptchaPublicConfig();
    await prefetchRecaptchaPublicConfig();
    const { token: captcha_token, executeError } = await getRecaptchaTokenResult("vin_decode");
    if (!captcha_token && executeError) {
      return {
        kind: "recaptcha_client",
        message: executeError,
      };
    }
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ vin, captcha_token }),
    });
    return { kind: "http", res };
  };

  let res;
  try {
    const first = await postOnce();
    if (first.kind === "recaptcha_client") {
      return {
        ok: false,
        status: 0,
        data: {
          success: false,
          error: first.message,
          recaptchaClientError: isRecaptchaClientSetupErrorMessage(first.message),
        },
      };
    }
    res = first.res;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return {
      ok: false,
      status: 0,
      data: {
        success: false,
        error: `Nu am putut contacta API-ul (${msg}). Verifică REACT_APP_API_BASE_URL (ex. http://localhost:8000/api) și că serverul PHP rulează.`,
      },
    };
  }

  let parsed = await readVinPartsResponse(res, url);

  const originBlocked =
    res.status === 403 &&
    typeof parsed.data?.origin === "string" &&
    parsed.data.origin.length > 0;

  if (!parsed.ok && res.status === 403 && !originBlocked) {
    await new Promise((r) => setTimeout(r, 450));
    try {
      const second = await postOnce();
      if (second.kind === "http") {
        parsed = await readVinPartsResponse(second.res, url);
      }
    } catch {
      /* păstrăm primul răspuns */
    }
  }

  return parsed;
}

export async function getParts(vehicleId, category) {
  const url = new URL(endpoint("parts.php"));
  url.searchParams.set("vehicleId", String(vehicleId));
  url.searchParams.set("category", category);
  const captcha_token = await getRecaptchaToken("vin_parts");
  url.searchParams.set("captcha_token", captcha_token);
  const urlStr = url.toString();
  let res;
  try {
    res = await fetch(urlStr);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return {
      ok: false,
      data: { success: false, error: `Rețea: ${msg}` },
    };
  }
  return readVinPartsResponse(res, urlStr);
}

export async function getSearchProducts(oem) {
  const url = new URL(endpoint("search-products.php"));
  url.searchParams.set("oem", oem);
  const captcha_token = await getRecaptchaToken("vin_products");
  url.searchParams.set("captcha_token", captcha_token);
  const urlStr = url.toString();
  let res;
  try {
    res = await fetch(urlStr);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return {
      ok: false,
      data: { success: false, error: `Rețea: ${msg}` },
    };
  }
  return readVinPartsResponse(res, urlStr);
}
